'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import tkns from '@lualtek/tokens/web/tokens.json';
import clsx from 'clsx';
import { forwardRef, useMemo } from 'react';
import { Glow, useStyles, } from '../../index.js';
import { ConditionalWrapper } from '../conditional-wrapper/index.js';
import styles from './panel.module.css';
export const Panel = forwardRef(({ as: Component = 'div', className, children, style, vibrant = false, vibrancyLevel = 'strong', vibrancyColor, vibrancySaturation, bordered, borderSide = 'all', radius, hPadding, vPadding, backgroundColor, backgroundColorHover, showGlow = false, glowSpread, glowColor, glowFitContent = false, rainbowColors, ...otherProps }, forwardedRef) => {
    const computedBackground = typeof backgroundColor === 'number' ? `var(--dimmed-${backgroundColor})` : backgroundColor;
    const computedBackgroundHover = typeof backgroundColorHover === 'number' ? `var(--dimmed-${backgroundColorHover})` : backgroundColorHover;
    const formatRadius = useMemo(() => {
        if (!radius) {
            return undefined;
        }
        if (!Array.isArray(radius)) {
            return tkns.radius[radius];
        }
        return radius.map(r => (r !== 0 ? tkns.radius[r] : 0)).join(' ');
    }, [radius]);
    const { vibrancy } = useStyles({
        vibrancy: {
            blur: vibrancyLevel,
            saturation: vibrancySaturation,
            color: vibrancyColor,
        },
    });
    const dynamicStyle = useMemo(() => {
        const getPaddingValue = (padding, direction) => {
            const [start, end] = Array.isArray(padding) ? padding : [padding, padding];
            return direction === 'horizontal'
                ? { left: start ? tkns.space[start] : 0, right: end ? tkns.space[end] : 0 }
                : { top: start ? tkns.space[start] : 0, bottom: end ? tkns.space[end] : 0 };
        };
        const vPaddingValues = vPadding ? getPaddingValue(vPadding, 'vertical') : { top: 0, bottom: 0 };
        const hPaddingValues = hPadding ? getPaddingValue(hPadding, 'horizontal') : { left: 0, right: 0 };
        return {
            '--v-padding-top': vPaddingValues.top,
            '--v-padding-bottom': vPaddingValues.bottom,
            '--h-padding-left': hPaddingValues.left,
            '--h-padding-right': hPaddingValues.right,
            '--radius': radius && formatRadius,
            '--background': vibrant ? undefined : computedBackground,
            '--background-hover': vibrant ? undefined : computedBackgroundHover,
        };
    }, [computedBackground, computedBackgroundHover, formatRadius, hPadding, radius, vPadding, vibrant]);
    return (_jsx(ConditionalWrapper, { condition: showGlow, wrapper: children => (_jsx(Glow, { innerRadius: radius, glowColor: glowColor ?? 'var(--dimmed-2)', spread: glowSpread, glowPower: 0, borderOffset: -1, borderWidth: 1, rainbowColors: rainbowColors, fitContent: glowFitContent, children: children })), children: _jsx(Component, { ref: forwardedRef, className: clsx(styles.Panel, className), "data-panel-bordered": bordered, "data-panel-border-side": borderSide, "data-panel-radius": Boolean(radius), "data-panel-hover": Boolean(backgroundColorHover), style: { ...dynamicStyle, ...style }, ...vibrant && vibrancy.attributes, ...otherProps, children: children }) }));
});
